// split-test: iscroll_modal

/* eslint-disable no-undef */
import EzModalController from 'layouts/modal/modal-controller';
import { fetchState, setState } from 'lib/state-helpers';
import { getAjaxModal } from 'lib/ajax-helpers';
import {
  triggerCustomEvent,
  getContentType,
} from 'lib/helpers';
import { initTooltips } from 'ez-styles/assets/js/utils/tooltips';
import events from 'lib/events';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import {
  injectModalPrefetching,
  getContextPositionByHref,
} from 'lib/show-modal-helpers';
import {
  GTM_DATALAYER,
  PAGE_VIEW,
  SHOW_PAGE_MODAL_CLOSE,
  SHOW_PAGE_MODAL_NEXT_CLICK,
  SHOW_PAGE_MODAL_PREVIOUS_CLICK,
} from 'components/analytics/conversions/constants/actions';
import { SHOW_MODAL_LOADED } from 'lib/ez-custom-events';

export default class ResourceShowModalViewController extends EzModalController {
  static targets = [
    'adTemplate',
    'content',
    'navLinkNext',
    'navLinkPrev',
    'analyticsData',
    'gtagConfig',
  ];

  connect() {
    super.connect();

    this.resourceType = this.element.querySelector('.ez-resource-show').dataset.resourceType;
    this.contentType = getContentType();

    this.modalViewInit();
  }

  setupTracking() {
    const gtagConfig = this.gtagConfigTarget.innerHTML;

    const eventData = {
      event: GTM_DATALAYER,
      ...this.getModalViewAnalyticsDimensions(),
    };

    delete eventData.snowplow_collector;
    delete eventData.snowplow_app_id;

    this.sendPageView();
    eval(gtagConfig); // eslint-disable-line no-eval
    window.dataLayer.push(eventData);
  }

  getModalViewAnalyticsDimensions() {
    const metaTag = this.analyticsDataTarget;
    return JSON.parse(metaTag.getAttribute('content'));
  }

  modalViewInit() {
    triggerCustomEvent(SHOW_MODAL_LOADED);

    this.contentTarget.classList.remove('is-loading');

    this.setupPageState();

    this.suspendProperAds();

    this.initAds();

    window.addEventListener(events.show_page.modal.history_close_modal_view, this.historyClose);
    window.addEventListener(events.show_page.modal.reload_modal_view, this.reloadModalView);
    window.addEventListener(events.show_page.modal.update_resource_navigation_context, this.updateNavContext);

    this.setupNavPos();

    this.displayNavElements();
  }

  setupPageState() {
    this.modalTitle = decodeURI(this.element.getAttribute('data-modal-title'));
    this.modalPath = this.element.getAttribute('data-modal-path');
    this.referringTitle = document.body.getAttribute('data-referring-title');
    this.referringPath = document.body.getAttribute('data-referring-path');
    this.modalViewFromHistory = document.body.getAttribute('data-modal-view-from-history');

    if (this.element.querySelectorAll('.ez-tooltip-icon').length > 0) {
      initTooltips();
    }

    if ('turbolinks' in window.history) {
      this.previousStateObj = window.history.state;
    }

    if (!this.modalViewFromHistory) {
      window.history.pushState({
        modalData: {
          id: 'resource-show-modal-view',
          title: this.modalTitle,
          path: this.modalPath,
          contextPath: this.referringPath,
          navContext: fetchState('resources-nav-context'),
        },
      }, this.modalTitle, this.modalPath);
    } else {
      this.removeModalHistoryAttrFromBody();
    }

    // This must come *after* history change to make sure titles in history are correct
    document.title = this.modalTitle;

    this.setupTracking();
  }

  removeModalHistoryAttrFromBody() {
    delete document.querySelector('body').dataset.modalViewFromHistory;
  }

  setupNavPos() {
    this.contextState = window.history.state.navContext || fetchState('resources-nav-context');

    const curPos = parseInt(this.contextState.currentPosition, 10);

    this.currentResourcePath = this.contextState[curPos]?.href;

    this.prevResourcePath = this.contextState[curPos - 1]?.href;

    this.nextResourcePath = this.contextState[curPos + 1]?.href;
  }

  displayNavElements() {
    if (this.prevResourcePath) {
      this.navLinkPrevTargets.forEach((el) => {
        el.classList.remove('is-disabled');
      });
      window.addEventListener('keyup', this.keyboardNavLeft);
    }

    if (this.nextResourcePath) {
      this.navLinkNextTargets.forEach((el) => {
        el.classList.remove('is-disabled');
      });
      window.addEventListener('keyup', this.keyboardNavRight);
    }
  }

  keyboardNavLeft = (event) => {
    if (event.keyCode === 37) {
      this.navLinkPrevTarget.classList.add('is-active');
      this.navLinkPrevTarget.click();
    }
  }

  keyboardNavRight = (event) => {
    if (event.keyCode === 39) {
      this.navLinkNextTarget.classList.add('is-active');
      this.navLinkNextTarget.click();
    }
  }

  navigate = (event) => {
    event.preventDefault();

    const navDirection = event.currentTarget.getAttribute('data-direction');

    const targetResourcePath = this[`${navDirection}ResourcePath`];

    triggerCustomEvent(events.show_page.modal.update_resource_navigation_context,
      { newCurrentPath: targetResourcePath });

    triggerCustomEvent(events.show_page.modal.reload_modal_view);

    triggerCustomEvent(events.show_page.modal[`${navDirection}_arrow_click`], {}); // split-test: iscroll_modal

    this.sendNavigationTracking(event.currentTarget, navDirection);

    getAjaxModal(targetResourcePath, injectModalPrefetching);
  }

  updateNavContext = (event) => {
    const { newCurrentPath } = event.detail;

    this.contextState.currentPosition = getContextPositionByHref(this.contextState, newCurrentPath);

    setState('resources-nav-context', this.contextState);
  }

  sendNavigationTracking(currentTarget, navDirection) {
    const direction = navDirection === 'prev' ? SHOW_PAGE_MODAL_PREVIOUS_CLICK : SHOW_PAGE_MODAL_NEXT_CLICK;

    sendTrackingEvent({
      element: currentTarget,
      data: {
        action: direction,
        label: this.resourceType,
        content_type: `Content-${this.contentType}`,
      },
    });
  }

  initAds() {
    if (typeof window.properSpaNewPage === 'undefined') return;

    const adTemplates = document.querySelector('[data-proper-ad-id]') !== null;

    if (adTemplates) window.properSpaNewPage();
  }

  emptyAdSlot(element) {
    const adunitInnerWrap = element.querySelector('.ez-ad-container__ad-wrap--inner');

    adunitInnerWrap.innerHTML = '';

    if (element.querySelector('.ad-sticky')) {
      element.querySelector('.ad-sticky').remove();
    }

    if (adunitInnerWrap.classList.contains('proper-dynamic-insertion')) {
      adunitInnerWrap.classList.remove('proper-dynamic-insertion');
    }
  }

  unsetProperAd(element) {
    this.emptyAdSlot(element);

    const clonedElement = element.cloneNode(true);
    const adSlotTemplate = document.createElement('template');

    adSlotTemplate.classList = 'ad-slot-template';
    adSlotTemplate.content.append(clonedElement);

    element.parentNode.insertBefore(adSlotTemplate, element);
    element.remove();

    window.properDeleteSlot(element.dataset.properAdId);
  }

  suspendProperAds() {
    if (typeof window.properDeleteSlot === 'undefined') return;

    const properAds = document.querySelectorAll('.ez-page-wrapper [data-proper-ad-id]');

    if (properAds) {
      properAds.forEach((properAd) => {
        this.unsetProperAd(properAd);
      });
    }
  }

  reinitProperAds() {
    const properAdTemplates = document.querySelectorAll('.ad-slot-template');

    properAdTemplates.forEach((properAdTemplate) => {
      const adSlotWrapper = properAdTemplate.parentNode;
      const adSlotContent = properAdTemplate.content.cloneNode(true);

      adSlotWrapper.insertBefore(adSlotContent, properAdTemplate);
      properAdTemplate.remove();
    });

    this.initAds();
  }

  reloadModalView = () => {
    // delay the loading overlay/spinner here, so fast internet users don't see it
    setTimeout(() => {
      this.contentTarget.classList.add('is-loading');
      this.addLoadingOverlay();
    }, 1000);

    this.cleanupEventListeners();
  }

  addLoadingOverlay() {
    const modalWrap = this.element.querySelector('.ez-modal__modal');
    this.loadingOverlayHtml = '<div class="resource-show-modal-view__loading-overlay" data-resource-show-modal-view-target="loadingOverlay"><div class="ez-spinner ez-spinner"></div></div>';

    modalWrap.insertAdjacentHTML(
      'afterBegin',
      this.loadingOverlayHtml,
    );
  }

  sendPageView() {
    sendTrackingEvent({
      data: {
        action: PAGE_VIEW,
        category: 'Show - Modal',
        content_group5: 'Show - Modal',
        page_path: window.location.pathname,
        dimension20: this.contentType,
        ...this.getModalViewAnalyticsDimensions(),
      },
    });
  }

  close = (event = null) => {
    super.close(event, this.beforeClose, this.afterClose);
  }

  historyClose = (event) => {
    triggerCustomEvent(events.modal.close_modal, { historyClose: true, ...event.detail });
  }

  beforeClose = (event) => {
    const eventDetail = (event && (typeof event.detail !== 'undefined')) ? event.detail : {};

    const updateState = typeof eventDetail.updateState !== 'undefined' ? eventDetail.updateState : true;

    const historyClose = eventDetail.historyClose || false;

    this.cleanup(historyClose, updateState);

    sendTrackingEvent({
      element: event.currentTarget,
      data: {
        action: SHOW_PAGE_MODAL_CLOSE,
        label: this.resourceType,
        content_type: `Content-${this.contentType}`,
      },
    });
  }

  afterClose = () => {
    this.reinitProperAds();
  }

  cleanup(historyClose, updateState) {
    this.cleanupEventListeners();

    if (updateState) {
      const newState = this.previousStateObj || {};

      if (historyClose) {
        window.history.replaceState(newState, this.referringTitle, this.referringPath);
      } else {
        window.history.pushState(newState, this.referringTitle, this.referringPath);
      }
    }

    // This must come *after* history change to make sure titles in history are correct
    document.title = this.referringTitle;
  }

  cleanupEventListeners() {
    window.removeEventListener(events.show_page.modal.history_close_modal_view, this.historyClose);
    window.removeEventListener(events.show_page.modal.reload_modal_view, this.reloadModalView);
    window.removeEventListener(events.show_page.modal.update_resource_navigation_context, this.updateNavContext);
    window.removeEventListener('keyup', this.keyboardNavLeft);
    window.removeEventListener('keyup', this.keyboardNavRight);
  }
}
/* eslint-enable no-undef */
